// Regras de carros por horário (ADR 001, docs/operations/technical.md), sem Vue
// para poderem ser testadas com `npm run test:unit`.
import moment from 'moment'

export const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

// Cópia de src/utils/capacity/defaultAvailability.ts da bocapio-api-2.0: grade
// que a API usa para produto sem availability. Manter igual à de lá.
export const DEFAULT_AVAILABILITY = {
  sunday: [{ start: '08:00', end: '17:00' }],
  monday: [{ start: '08:00', end: '17:00' }],
  tuesday: [{ start: '08:00', end: '17:00' }],
  wednesday: [{ start: '08:00', end: '17:00' }],
  thursday: [{ start: '08:00', end: '17:00' }],
  friday: [
    { start: '08:00', end: '12:00' },
    { start: '12:00', end: '17:00' },
  ],
  saturday: [{ start: '08:00', end: '17:00' }],
}

export function normalizeTimeKey(start) {
  if (start == null || start === '*' || start === '') return ''
  const parsed = moment(String(start), ['HH:mm:ss', 'HH:mm', 'H:mm'], true)
  return parsed.isValid() ? parsed.format('HH:mm') : ''
}

export function bookingPeopleCount(booking) {
  const tableText = booking.people && booking.people.tableText
  return (tableText && Number(tableText.count)) || 0
}

// Condutor manual da reserva: tipo ausente/'driver', ativo e não é a cópia do
// motorista do carro que a API grava (source 'vehicle'). Mesma regra da API
// (modules/booking/domain/services/vehicleAllocation.ts).
export function isManualDriver(agent) {
  return (
    (agent.type === undefined || agent.type === 'driver') &&
    agent.active !== false &&
    agent.source !== 'vehicle'
  )
}

// uuids dos motoristas dos carros do horário (resources do bloco)
export function blockDriverUuids(vehicleResources) {
  return new Set(
    (vehicleResources || [])
      .map((item) => item && item.driver && item.driver.uuid)
      .filter(Boolean)
  )
}

// Condutores manuais (sem repetição) que não dirigem nenhum carro do horário:
// viajam como passageiros e ocupam lugar (ADR 001)
export function extraDriverCount(booking, driverUuids) {
  return new Set(
    (booking.agents || [])
      .filter(isManualDriver)
      .map((agent) => agent.uuid)
      .filter((uuid) => uuid && !driverUuids.has(uuid))
  ).size
}

// Lugares da reserva = pessoas vendidas + condutores extras
export function bookingSeats(booking, driverUuids = new Set()) {
  return bookingPeopleCount(booking) + extraDriverCount(booking, driverUuids)
}

// Lugares da reserva por veículo. Um veículo ativo sem pax é dado legado: a
// reserva inteira está nele. Vários ativos sem pax não dizem como os lugares se
// dividem, então nada é alocado.
export function bookingVehicleAllocations(booking, driverUuids = new Set()) {
  const total = bookingSeats(booking, driverUuids)
  const active = (booking.vehicles || []).filter((item) => item.active)
  if (active.length === 1 && active[0].pax == null) {
    return { allocations: [{ uuid: active[0].uuid, pax: total }], unallocated: 0 }
  }
  if (!active.length || active.some((item) => item.pax == null)) {
    return { allocations: [], unallocated: total }
  }
  const allocations = active.map((item) => ({
    uuid: item.uuid,
    pax: Number(item.pax) || 0,
  }))
  const allocated = allocations.reduce((sum, item) => sum + item.pax, 0)
  return { allocations, unallocated: Math.max(0, total - allocated) }
}

function catalogVehicle(vehicle, catalog) {
  if (!vehicle) return null
  const key = vehicle.key || vehicle.uuid || vehicle.resource_uuid
  const list = catalog || []
  const named = list.find(
    (item) => item && item.uuid === key && vehicle.name && item.name === vehicle.name
  )
  return named || list.find((item) => item && item.uuid === key) || null
}

// O lápis de motorista some quando o veículo não precisa de condutor.
// false no recurso do bloco vale primeiro; senão, o cadastro pelo uuid.
// Sem os dois, o lápis continua.
export function vehicleRequiresDriver(vehicle, catalog = []) {
  if (!vehicle || vehicle.driver_required === false) return false
  if (vehicle.driver_required === true) return true
  const match = catalogVehicle(vehicle, catalog)
  if (match && match.driver_required === false) return false
  return true
}

// Categoria de combustível do chip. O recurso do bloco vale primeiro; senão o
// cadastro pelo uuid. "A distribuir" não é veículo.
export function vehicleFuelCategory(vehicle, catalog = []) {
  if (!vehicle || vehicle.key === 'a-distribuir') return ''
  const own = String(vehicle.fuel_category || '').trim()
  if (own) return own
  const match = catalogVehicle(vehicle, catalog)
  return (match && String(match.fuel_category || '').trim()) || ''
}

// Descrição do cadastro. O recurso do bloco vale primeiro; senão o catálogo.
// "A distribuir" não é veículo.
export function vehicleDescription(vehicle, catalog = []) {
  if (!vehicle || vehicle.key === 'a-distribuir') return ''
  const own = String(vehicle.description || '').trim()
  if (own) return own
  const match = catalogVehicle(vehicle, catalog)
  return (match && String(match.description || '').trim()) || ''
}

// Capacidade do cadastro. null quando o veículo não está no catálogo.
// 0 é capacidade dinâmica (Trilha, Off-road): sem vaga fixa.
export function vehicleRegisteredCapacity(vehicle, catalog = []) {
  const match = catalogVehicle(vehicle, catalog)
  if (!match || match.capacity == null || match.capacity === '') return null
  const capacity = Number(match.capacity)
  return Number.isFinite(capacity) ? capacity : null
}

// Distribui os lugares das reservas entre os veículos do horário. Quem não tem
// carro, está em carro fora do horário ou sobrou da divisão vai para toDistribute.
// O motorista do carro (driver) não ocupa lugar.
export function allocateSlotVehicles(vehicleResources, bookings) {
  const driverUuids = blockDriverUuids(vehicleResources)
  const emptyVehicle = (fields) => ({
    people: 0,
    driver: null,
    bookings: [],
    paxByHash: {},
    ...fields,
  })
  const vehicles = vehicleResources.map((item) =>
    emptyVehicle({
      key: item.resource_uuid || item.resource_plate,
      name: item.resource_name || item.resource_plate,
      plate: item.resource_plate,
      capacity: Number(item.resource_capacity) || 0,
      driver: item.driver || null,
      driver_required: item.driver_required,
      fuel_category: item.fuel_category || '',
      description: item.description || item.resource_description || '',
    })
  )
  const toDistribute = emptyVehicle({
    key: 'a-distribuir',
    name: 'Aguardando',
    capacity: 0,
  })
  const place = (vehicle, booking, pax) => {
    if (!vehicle.paxByHash[booking.hash]) vehicle.bookings.push(booking)
    vehicle.paxByHash[booking.hash] =
      (vehicle.paxByHash[booking.hash] || 0) + pax
    vehicle.people += pax
  }

  bookings.forEach((booking) => {
    const { allocations, unallocated } = bookingVehicleAllocations(
      booking,
      driverUuids
    )
    let missing = unallocated
    allocations.forEach((allocation) => {
      const vehicle = vehicles.find((item) => item.key === allocation.uuid)
      if (vehicle) place(vehicle, booking, allocation.pax)
      else missing += allocation.pax
    })
    if (missing > 0) place(toDistribute, booking, missing)
  })

  // Bloco sem carro: toda reserva fica em "A distribuir", mesmo com 0 lugares.
  if (!vehicles.length) {
    bookings.forEach((booking) => {
      if (toDistribute.bookings.includes(booking)) return
      toDistribute.bookings.push(booking)
      if (toDistribute.paxByHash[booking.hash] == null) {
        toDistribute.paxByHash[booking.hash] = 0
      }
    })
  }

  return { vehicles, toDistribute, driverUuids }
}

// Intervalo [start, end) do bloco, pela mesma grade que a API usa para barrar
// veículo em dois atrativos (productResources.service.ts): availability do dia
// da semana, ou DEFAULT_AVAILABILITY quando o produto não tem.
export function blockInterval(availability, dateKey, timeKey) {
  const weekday = WEEKDAYS[moment(dateKey, 'YYYY-MM-DD').day()]
  const slots =
    (availability && availability[weekday]) || DEFAULT_AVAILABILITY[weekday] || []
  const slot = slots.find(
    (item) => normalizeTimeKey(item && item.start) === timeKey
  )
  // Bloco fora da grade (dado inconsistente): vira intervalo vazio no início
  return { start: timeKey, end: slot ? normalizeTimeKey(slot.end) : timeKey }
}

// Fim aberto: 09–14 e 14–19 não se sobrepõem. Intervalo vazio (bloco fora da
// grade) conflita se o início cair dentro do outro, como faz a API.
export function intervalsOverlap(mine, other) {
  return other.end === other.start
    ? other.start >= mine.start && other.start < mine.end
    : mine.start < other.end && other.start < mine.end
}
